import { defineStore } from 'pinia'
import { store } from '@/store'
import { constantRoutes } from '@/router/index'
import { hasPerm } from '@/utils/index'
import frontRoutes from '@/router/front'
import defaultSettings from '@/settings'
import { listApi as menuListApi } from '@/api/system/menu'

const modules = import.meta.glob('../../views/**/**.vue')
const Layout = () => import('@/layout/index.vue')

const manualReviewLocalRoutes = [
  {
    path: '/business/review-lawyer/list',
    name: 'BusinessReviewLawyerList',
    component: modules['../../views/business/review-lawyer/list.vue'],
    meta: {
      icon: 'UserFilled',
      title: '人工复核律师',
      roles: ['admin/business.Review/list'],
      activeMenu: '/business/review-lawyer/list',
      activeMenuTop: '/business',
      hidden: false,
      keepAlive: true,
      alwaysShow: false
    }
  },
  {
    path: '/business/review-lawyer/info/:id',
    name: 'BusinessReviewLawyerInfo',
    component: modules['../../views/business/review-lawyer/info.vue'],
    meta: {
      title: '律师详情',
      roles: ['admin/business.Review/list'],
      activeMenu: '/business/review-lawyer/list',
      activeMenuTop: '/business',
      hidden: true,
      keepAlive: true,
      alwaysShow: false
    }
  },
  {
    path: '/business/review-claims/list',
    name: 'BusinessReviewClaims',
    component: modules['../../views/business/review-claims/list.vue'],
    meta: {
      icon: 'Finished',
      title: '我的接单',
      roles: ['admin/business.Review/list'],
      activeMenu: '/business/review-claims/list',
      activeMenuTop: '/business',
      hidden: false,
      keepAlive: true,
      alwaysShow: false
    }
  }
]

/**
 * 后端动态菜单尚未配置新页面时，将本地页面挂到已有人工复核菜单旁。
 * 只有已经拥有人工复核列表入口的用户才会获得这些路由。
 */
function injectManualReviewRoutes(routes) {
  function findReviewList(routeList) {
    for (const route of routeList || []) {
      const isReviewList =
        route.name === 'BusinessReviewList' ||
        route.path === '/business/review/list' ||
        route.meta?.title === '人工复核列表'
      if (isReviewList) return { route, siblings: routeList }
      const match = findReviewList(route.children)
      if (match) return match
    }
    return null
  }

  const match = findReviewList(routes)
  if (!match) return routes

  const existingNames = new Set()
  function collectNames(routeList) {
    for (const route of routeList || []) {
      if (route.name) existingNames.add(route.name)
      collectNames(route.children)
    }
  }
  collectNames(routes)

  const baseSort = Number(match.route.sort ?? match.route.meta?.sort ?? 999999)
  const additions = manualReviewLocalRoutes
    .filter((route) => !existingNames.has(route.name))
    .map((route, index) => ({
      ...route,
      sort: baseSort + index + 1,
      meta: { ...route.meta, sort: baseSort + index + 1 }
    }))

  const reviewIndex = match.siblings.indexOf(match.route)
  match.siblings.splice(reviewIndex + 1, 0, ...additions)
  return routes
}

/**
 * 递归生成异步(动态)路由
 * @param routes 接口返回的异步(动态)路由
 * @param parentPath 父级 path（用于 component 为空时推断，如 business）
 * @returns 异步(动态)路由
 */
const filterAsyncRoutes = (routes, parentPath = '') => {
  const asyncRoutes = []
  const sortedRoutes = [...routes].sort((a, b) => {
    const sortA = a.sort ?? a.meta?.sort ?? 999999
    const sortB = b.sort ?? b.meta?.sort ?? 999999
    const numA = Number(sortA) || 999999
    const numB = Number(sortB) || 999999
    return numA - numB
  })

  sortedRoutes.forEach((route) => {
    const tmpRoute = { ...route }
    const isExternalPath = tmpRoute.path && /^https?:\/\//i.test(tmpRoute.path)
    const externalLink = isExternalPath ? tmpRoute.path : ''
    if (externalLink) {
      tmpRoute.path = `/external/${encodeURIComponent(externalLink)}`
      if (tmpRoute.meta) {
        tmpRoute.meta = { ...tmpRoute.meta, externalLink }
      } else {
        tmpRoute.meta = { externalLink }
      }
    }

    let fullPath = ''
    if (tmpRoute.path) {
      if (tmpRoute.path.startsWith('/')) {
        fullPath = tmpRoute.path
      } else if (parentPath) {
        fullPath = ('/' + parentPath + '/' + tmpRoute.path).replace(/\/+/g, '/')
      } else {
        fullPath = '/' + tmpRoute.path
      }
    }

    if (tmpRoute.meta) {
      if (!tmpRoute.meta.activeMenu || tmpRoute.meta.activeMenu === '') {
        tmpRoute.meta.activeMenu = fullPath
      }
    }

    if (tmpRoute.component?.toString() == 'Layout') {
      tmpRoute.component = Layout
      if (!tmpRoute.redirect || tmpRoute.redirect === 'noRedirect') {
        const firstVisibleChild = (tmpRoute.children || []).find((c) => !c.meta?.hidden)
        if (firstVisibleChild && firstVisibleChild.path) {
          const childPath = firstVisibleChild.path.startsWith('/')
            ? firstVisibleChild.path
            : tmpRoute.path + '/' + firstVisibleChild.path
          tmpRoute.redirect = childPath.replace(/\/+/g, '/')
        }
      }
    } else {
      if (tmpRoute?.component) {
        const component = modules[`../../views/${tmpRoute.component}.vue`]
        if (component) {
          tmpRoute.component = component
        } else {
          tmpRoute.component = modules[`../../views/system/system/404.vue`]
        }
      } else {
        if (externalLink) {
          tmpRoute.component = Layout
        } else if (parentPath && tmpRoute.path) {
          const inferred = (parentPath + '/' + tmpRoute.path).replace(/\/+/g, '/').replace(/^\//, '')
          const component = modules[`../../views/${inferred}.vue`]
          if (component) {
            tmpRoute.component = component
          } else {
            tmpRoute.component = modules[`../../views/system/system/404.vue`]
          }
        } else {
          tmpRoute.component = modules[`../../views/system/system/404.vue`]
        }
      }
    }

    const nextParent =
      tmpRoute.path && !externalLink
        ? (parentPath ? parentPath + '/' + tmpRoute.path : tmpRoute.path).replace(/\/+/g, '/').replace(/^\//, '')
        : parentPath
    if (tmpRoute.children) {
      tmpRoute.children = filterAsyncRoutes(tmpRoute.children, nextParent)
    }

    asyncRoutes.push(tmpRoute)
  })

  return asyncRoutes
}

/**
 * @param routes
 */
export function filterFrontRoutes(routes) {
  const res = []

  // 先对路由进行排序，按照 sort 值排序
  const sortedRoutes = [...routes].sort((a, b) => {
    const sortA = a.sort ?? a.meta?.sort ?? 999999
    const sortB = b.sort ?? b.meta?.sort ?? 999999
    const numA = Number(sortA) || 999999
    const numB = Number(sortB) || 999999
    return numA - numB
  })

  sortedRoutes.forEach((route) => {
    const tmp = { ...route }
    // 先递归处理子路由
    if (tmp.children && tmp.children.length > 0) {
      tmp.children = filterFrontRoutes(tmp.children)
    }
    // 如果 roles 为空数组或未定义，视为不需要权限检查，直接显示
    const roles = route.meta?.roles
    const noPermissionCheck = !roles || (Array.isArray(roles) && roles.length === 0)
    // 如果父路由有权限，或者不需要权限检查，或者子路由有权限（过滤后不为空），则显示该路由
    if (noPermissionCheck || hasPerm(roles) || (tmp.children && tmp.children.length > 0)) {
      res.push(tmp)
    }
  })

  return res
}

// setup
export const usePermissionStore = defineStore('permission', () => {
  // state
  // 路由
  const routes = ref([])
  // 混合模式左侧菜单
  const mixLeftMenu = ref([])

  // actions

  /**
   * 设置路由
   * @param newRoutes 新的路由列表
   * @param sortMaps 可选的 sort 映射表，用于设置首页路由的 sort 值
   */
  function setRoutes(newRoutes, sortMaps = null) {
    // 检查 newRoutes 中是否已经包含首页路由（path 为 '/'）
    const hasHomeRoute = newRoutes.some((route) => route.path === '/')

    // 如果 newRoutes 中已经有首页路由，过滤掉 constantRoutes 中的首页路由，避免重复
    const filteredConstantRoutes = hasHomeRoute ? constantRoutes.filter((route) => route.path !== '/') : constantRoutes

    const allRoutes = filteredConstantRoutes.concat(newRoutes)

    // 如果有 sortMaps，处理首页路由的 sort 值
    if (sortMaps) {
      const { pathSortMap, titleSortMap } = sortMaps
      allRoutes.forEach((route) => {
        // 处理首页路由（path 为 '/'）
        if (route.path === '/') {
          const homeSort = pathSortMap['/'] ?? pathSortMap['/dashboard'] ?? titleSortMap['首页'] ?? 999999
          route.sort = homeSort
          if (route.meta) {
            route.meta.sort = homeSort
          } else {
            route.meta = { sort: homeSort }
          }
        }
      })
    }

    routes.value = allRoutes
  }
  /**
   * 从后端菜单数据中创建 sort 映射表
   * @param menus 后端菜单数据（扁平列表）
   * @returns {Object} 包含 path、name 和 title 的 sort 映射表
   */
  function createMenuSortMap(menus) {
    const pathSortMap = {} // 路径 -> sort值
    const nameSortMap = {} // name -> sort值
    const titleSortMap = {} // menu_name -> sort值

    // 判断是扁平列表还是树形结构
    const isFlatList = Array.isArray(menus) && menus.length > 0 && menus[0].menu_pid !== undefined

    if (isFlatList) {
      // 找到所有菜单ID，用于判断哪些菜单是父菜单
      const allMenuIds = new Set(menus.map((m) => m.menu_id))

      // 找出所有 menu_pid，检查哪些 menu_pid 对应的菜单不在列表中（这些是一级菜单的父菜单）
      const parentMenuIds = new Set(menus.map((m) => m.menu_pid))
      const missingParentIds = [...parentMenuIds].filter((pid) => pid && !allMenuIds.has(pid))

      // 一级菜单的判断：
      // 1. menu_pid 为 0 的菜单，且 menu_type 为 0（目录类型），且有 path
      // 2. 首页：menu_pid 为 0，menu_type 为 2，menu_name 为 "首页"
      const topMenus = menus.filter((menu) => {
        // 目录类型的一级菜单
        if (menu.menu_pid === 0 && menu.menu_type === 0 && menu.path) {
          return true
        }
        // 首页（menu_type 为 2，menu_name 为 "首页"）
        if (menu.menu_pid === 0 && menu.menu_type === 2 && menu.menu_name === '首页') {
          return true
        }
        return false
      })

      menus.forEach((menu) => {
        // 直接使用 sort 字段
        const sortValue = menu.sort ?? 999999
        const sortNum = Number(sortValue) || 999999

        // 通过 menu_name 建立映射
        if (menu.menu_name) {
          titleSortMap[menu.menu_name] = sortNum
        }

        // 特殊处理首页：menu_name 为 "首页" 或 menu_type 为 2 且 menu_pid 为 0
        if (menu.menu_name === '首页' || (menu.menu_type === 2 && menu.menu_pid === 0)) {
          pathSortMap['/'] = sortNum
          pathSortMap['/dashboard'] = sortNum
          pathSortMap['dashboard'] = sortNum
          titleSortMap['首页'] = sortNum
        }

        if (menu.path) {
          // 处理路径：如果是相对路径，需要拼接父路径
          let fullPath = menu.path
          if (!menu.path.startsWith('/')) {
            // 如果是扁平列表，需要根据 menu_pid 找到父路径
            if (menu.menu_pid && menu.menu_pid > 0) {
              const parentMenu = menus.find((m) => m.menu_id === menu.menu_pid)
              if (parentMenu && parentMenu.path) {
                const parentPath = parentMenu.path.startsWith('/') ? parentMenu.path : '/' + parentMenu.path
                fullPath = parentPath + '/' + menu.path
              } else {
                fullPath = '/' + menu.path
              }
            } else {
              fullPath = '/' + menu.path
            }
          }

          // 使用完整路径作为 key
          pathSortMap[fullPath] = sortNum
          // 也使用原始 path 作为 key
          pathSortMap[menu.path] = sortNum

          // 特殊处理：如果路径是 /index-config，也映射到 /config（前端路由使用的是 /config）
          if (fullPath === '/index-config' || menu.path === '/index-config' || menu.path === 'index-config') {
            pathSortMap['/config'] = sortNum
            pathSortMap['config'] = sortNum
          }

          // 如果有 name，也建立映射
          if (menu.name) {
            nameSortMap[menu.name] = sortNum
          }
        }
      })
    } else {
      // 处理树形结构
      function traverse(menuList, parentPath = '') {
        if (!Array.isArray(menuList)) return
        menuList.forEach((menu) => {
          // 直接使用 sort 字段
          const sortValue = menu.sort ?? 999999
          const sortNum = Number(sortValue) || 999999

          // 通过 menu_name 建立映射
          if (menu.menu_name) {
            titleSortMap[menu.menu_name] = sortNum
          }
          // 也通过 meta.title 建立映射（如果存在）
          if (menu.meta?.title) {
            titleSortMap[menu.meta.title] = sortNum
          }

          // 特殊处理首页：menu_name 为 "首页" 或 menu_type 为 2
          if (menu.menu_name === '首页' || menu.meta?.title === '首页' || menu.menu_type === 2) {
            pathSortMap['/'] = sortNum
            pathSortMap['/dashboard'] = sortNum
            pathSortMap['dashboard'] = sortNum
            titleSortMap['首页'] = sortNum
          }

          if (menu.path) {
            // 处理路径：如果是相对路径，需要拼接父路径
            let fullPath = menu.path
            if (!menu.path.startsWith('/') && parentPath) {
              fullPath = parentPath + '/' + menu.path
            } else if (!menu.path.startsWith('/')) {
              fullPath = '/' + menu.path
            }

            // 使用完整路径作为 key
            pathSortMap[fullPath] = sortNum
            // 也使用原始 path 作为 key
            pathSortMap[menu.path] = sortNum

            // 特殊处理：如果路径是 /index-config，也映射到 /config（前端路由使用的是 /config）
            if (fullPath === '/index-config' || menu.path === '/index-config' || menu.path === 'index-config') {
              pathSortMap['/config'] = sortNum
              pathSortMap['config'] = sortNum
            }

            // 如果有 name，也建立映射
            if (menu.name) {
              nameSortMap[menu.name] = sortNum
            }
          }
          // 递归处理子路由
          if (menu.children && menu.children.length > 0) {
            const currentPath = menu.path?.startsWith('/')
              ? menu.path
              : parentPath
                ? parentPath + '/' + menu.path
                : menu.path
            traverse(menu.children, currentPath)
          }
        })
      }

      traverse(menus)
    }

    return { pathSortMap, nameSortMap, titleSortMap }
  }

  /**
   * 将后端菜单的 sort 值合并到前端路由
   * @param routes 前端路由
   * @param sortMaps 菜单 sort 映射表
   * @param parentPath 父路径
   */
  function mergeMenuSort(routes, sortMaps, parentPath = '') {
    if (!Array.isArray(routes)) return routes
    const { pathSortMap, nameSortMap, titleSortMap } = sortMaps

    return routes.map((route) => {
      const newRoute = { ...route }

      // 构建完整路径用于匹配
      let fullPath = route.path
      if (route.path && !route.path.startsWith('/') && !route.path.startsWith('http') && parentPath) {
        fullPath = parentPath + '/' + route.path
      } else if (route.path && !route.path.startsWith('/') && !route.path.startsWith('http')) {
        fullPath = '/' + route.path
      }

      // 按优先级尝试匹配 sort 值：
      // 1. 通过 path 匹配（最高优先级）
      // 2. 通过 name 匹配
      // 3. 通过 meta.title 匹配 menu_name
      let sortValue = null
      if (route.path) {
        sortValue = pathSortMap[fullPath] ?? pathSortMap[route.path]
      }
      if (sortValue === null && route.name) {
        sortValue = nameSortMap[route.name]
      }
      if (sortValue === null && route.meta?.title) {
        sortValue = titleSortMap[route.meta.title]
      }

      // 如果找到了 sort 值，设置到路由中
      if (sortValue !== null && sortValue !== undefined) {
        newRoute.sort = sortValue
        if (newRoute.meta) {
          newRoute.meta.sort = sortValue
        } else {
          newRoute.meta = { sort: sortValue }
        }
      } else {
        // 如果没有匹配到，使用默认值
        newRoute.sort = 999999
        if (newRoute.meta) {
          newRoute.meta.sort = 999999
        } else {
          newRoute.meta = { sort: 999999 }
        }
      }

      // 递归处理子路由
      if (newRoute.children && newRoute.children.length > 0) {
        const currentPath = newRoute.path?.startsWith('/')
          ? newRoute.path
          : parentPath
            ? parentPath + '/' + newRoute.path
            : newRoute.path
        newRoute.children = mergeMenuSort(newRoute.children, sortMaps, currentPath)
      }

      return newRoute
    })
  }

  /**
   * 生成路由
   */
  function generateRoutes(menus) {
    return new Promise((resolve, reject) => {
      if (defaultSettings.frontRouter) {
        // 前端路由：从菜单列表接口获取 sort 值并合并
        menuListApi({ islist: 1 })
          .then((res) => {
            const menuList = res.data?.list || []
            const sortMaps = createMenuSortMap(menuList)
            const routesWithSort = mergeMenuSort(frontRoutes, sortMaps)
            const accessedRoutes = filterFrontRoutes(routesWithSort)
            setRoutes(accessedRoutes, sortMaps)
            resolve(accessedRoutes)
          })
          .catch((err) => {
            console.error('获取菜单列表失败，使用默认排序:', err)
            // 如果获取失败，使用用户菜单数据
            const sortMaps = createMenuSortMap(menus || [])
            const routesWithSort = mergeMenuSort(frontRoutes, sortMaps)
            const accessedRoutes = filterFrontRoutes(routesWithSort)
            setRoutes(accessedRoutes, sortMaps)
            resolve(accessedRoutes)
          })
      } else {
        // 后端路由：直接使用后端菜单数据
        const asyncRoutes = menus
        const accessedRoutes = injectManualReviewRoutes(filterAsyncRoutes(asyncRoutes))
        setRoutes(accessedRoutes)
        resolve(accessedRoutes)
      }
    })
  }
  /**
   * 获取混合模式左侧菜单
   */
  function getMixLeftMenu(activeTop) {
    routes.value.forEach((item) => {
      if (item.path === activeTop) {
        const children = item.children || []
        // 按 sort 值排序
        mixLeftMenu.value = children.sort((a, b) => {
          const sortA = a.sort ?? a.meta?.sort ?? 999999
          const sortB = b.sort ?? b.meta?.sort ?? 999999
          const numA = Number(sortA) || 999999
          const numB = Number(sortB) || 999999
          return numA - numB
        })
      }
    })
  }
  return { routes, mixLeftMenu, setRoutes, generateRoutes, getMixLeftMenu }
})

// 非setup
export function usePermissionStoreHook() {
  return usePermissionStore(store)
}
